import { Button, Modal, Row, Space } from "ant-design-vue";
import { CheckOutlined } from "@ant-design/icons-vue";
import { computed, defineComponent, onMounted, reactive, ref } from "vue";
import { useStore } from "vuex";
import { fmtMsg } from "vue-glcommonui";
import { ClassSetUp, CommonLocale } from "@/locales/localeid";
import DeviceList from "@/components/common/device-list/device-list.vue";
import { CallingDeviceType } from "@/agora";
import { useDeviceSettingModal } from "@/hooks/useDeviceSettingModal";
import { MODULES, vuexName } from "@/store/utils";

export default defineComponent({
  props: {
    isCamEnabled: {
      type: Boolean,
      required: false,
      default: true,
    },
    isMicEnabled: {
      type: Boolean,
      required: false,
      default: true,
    },
    onUpdateCamOpen: {
      type: Function,
      required: false,
    },
    onUpdateMicOpen: {
      type: Function,
      required: false,
    },
  },
  components: {
    Button,
    Modal,
    Row,
    Space,
    CheckOutlined,
    DeviceList,
  },
  setup(props) {
    const { getters, dispatch } = useStore();
    const { toggleModal } = useDeviceSettingModal();
    const deviceListCompRef = ref<InstanceType<typeof DeviceList> | null>(null);
    const currentIsCamEnabled = ref<Boolean>(props.isCamEnabled);
    const currentIsMicEnabled = ref<Boolean>(props.isMicEnabled);
    const isFooterVisible = ref(!!props.onUpdateCamOpen);
    const isDeviceError = ref(false);
    const initialDeviceIds = reactive<{ cam?: string; mic?: string; speaker?: string }>({});

    const onOpenMicChange = (currentOpenMicValue: boolean) => {
      currentIsMicEnabled.value = currentOpenMicValue;
    };

    const onOpenCamChange = (currentOpenCamValue: boolean) => {
      currentIsCamEnabled.value = currentOpenCamValue;
    };

    const handleDeviceError = (isError: boolean) => {
      isDeviceError.value = isError;
    };

    const isOpenCamChange = computed(() => props.isCamEnabled !== currentIsCamEnabled.value);
    const isOpenMicChange = computed(() => props.isMicEnabled !== currentIsMicEnabled.value);

    const isCamChanged = computed(() => {
      if (!initialDeviceIds.cam && !deviceListCompRef.value?.activeCameraId) {
        return false;
      }
      return initialDeviceIds.cam !== deviceListCompRef.value?.activeCameraId;
    });
    const isMicChanged = computed(() => {
      if (!initialDeviceIds.mic && !deviceListCompRef.value?.activeMicrophoneId) {
        return false;
      }
      return initialDeviceIds.mic !== deviceListCompRef.value?.activeMicrophoneId;
    });
    const isSpeakerChanged = computed(() => {
      if (!initialDeviceIds.speaker && !deviceListCompRef.value?.activeSpeakerId) {
        return false;
      }
      return initialDeviceIds.speaker !== deviceListCompRef.value?.activeSpeakerId;
    });

    const isUpdateBtnDisabled = computed(() => {
      return (
        isDeviceError.value ||
        (!isOpenCamChange.value && !isOpenMicChange.value && !isCamChanged.value && !isSpeakerChanged.value && !isMicChanged.value)
      );
    });

    const handleSubmitDeviceSetting = async () => {
      // Update new device ids
      await Promise.all([
        dispatch(vuexName(MODULES.APP.DISPATCHES.SET_CAMERA_DEVICE_ID, false), deviceListCompRef.value?.activeCameraId ?? ""),
        dispatch(vuexName(MODULES.APP.DISPATCHES.SET_MICROPHONE_DEVICE_ID, false), deviceListCompRef.value?.activeMicrophoneId ?? ""),
        dispatch(vuexName(MODULES.APP.DISPATCHES.SET_SPEAKER_DEVICE_ID, false), deviceListCompRef.value?.activeSpeakerId ?? ""),
      ]);

      if (isOpenCamChange.value && props.onUpdateCamOpen) {
        await props.onUpdateCamOpen(currentIsCamEnabled.value);
      }
      if (isOpenMicChange.value && props.onUpdateMicOpen) {
        await props.onUpdateMicOpen(currentIsMicEnabled.value);
      }
      if (isCamChanged.value) {
        await dispatch(vuexName(MODULES.CALLING.DISPATCHES.CALLING_UPDATE_DEVICES), {
          deviceType: CallingDeviceType.CAMERA,
          isTurnOff: !currentIsCamEnabled.value,
        });
      }
      if (isMicChanged.value) {
        await dispatch(vuexName(MODULES.CALLING.DISPATCHES.CALLING_UPDATE_DEVICES), { deviceType: CallingDeviceType.MICROPHONE });
      }
      if (isSpeakerChanged.value) {
        await dispatch(vuexName(MODULES.CALLING.DISPATCHES.CALLING_UPDATE_DEVICES), { deviceType: CallingDeviceType.SPEAKER });
      }

      toggleModal(false);
    };

    const setInitialDeviceIds = () => {
      initialDeviceIds.cam = getters[vuexName(MODULES.APP.GETTERS.GET_CAMERA_DEVICE_ID, false)];
      initialDeviceIds.mic = getters[vuexName(MODULES.APP.GETTERS.GET_MICROPHONE_DEVICE_ID, false)];
      initialDeviceIds.speaker = getters[vuexName(MODULES.APP.GETTERS.GET_SPEAKER_DEVICE_ID, false)];
    };

    onMounted(() => {
      currentIsCamEnabled.value = props.isCamEnabled;
      currentIsMicEnabled.value = props.isMicEnabled;

      setInitialDeviceIds();
    });

    const DeviceListText = computed(() => fmtMsg(ClassSetUp.DeviceSettings));
    const CommonUpdateText = computed(() => fmtMsg(CommonLocale.CommonUpdateButtonText));
    const CommonCancelText = computed(() => fmtMsg(CommonLocale.CommonCancelButtonText));

    return {
      toggleModal,
      isUpdateBtnDisabled,
      onOpenMicChange,
      onOpenCamChange,
      handleDeviceError,
      handleSubmitDeviceSetting,
      DeviceListText,
      CommonCancelText,
      CommonUpdateText,
      deviceListCompRef,
      isFooterVisible,
    };
  },
});
